/**
 * "Where are we?" in one line for the app bar — the release, the phase, and how much
 * of the deploy is done.
 *
 * It exists because the deploying half is the one part of this system an operator
 * walks away from: a compile takes twenty minutes, and they come back to a browser
 * tab, or open a second one, or look at the instruction page rather than the home
 * page. The badge answers "which release, and how far in" from any page, so the
 * answer never depends on having the right one open.
 *
 * Pure so the wording is tested rather than read off a screenshot.
 */
import type { Phase } from '@finvari/deployer';
import type { DeployProgress } from './componentRows';

/**
 * How each phase reads in the bar — short, since it sits beside the version. The
 * two `*_host_starting` phases are named for the machine in the state machine and
 * for what the operator does about them here: wake the system, or sync it. Which
 * machine is which is the host pages' business, not the badge's.
 */
const PHASE_LABEL: Record<Phase, string> = {
    idle: 'No release',
    prepare_host_starting: 'Waiting for the system',
    merging: 'Merging',
    capturing: 'Capturing',
    describing: 'Drafting',
    reviewing: 'In review',
    publishing: 'Publishing',
    release_host_starting: 'Waiting for the sync',
    open: 'Deploying',
    closing: 'Closing',
};

export interface HeaderBadge {
    /** The line itself, or undefined when there is nothing to say (idle). */
    text?: string;
    /** True in the deploying half, where the badge is the page an operator left. */
    emphasis: boolean;
}

export interface HeaderBadgeInput {
    phase: Phase;
    version: string | undefined;
    /** Only in the deploying half; absent before the host has synced. */
    progress?: DeployProgress;
}

/**
 * The bar's badge. Silent when idle: the home page already says there is no release,
 * and a permanent "No release" chip is a label an operator learns to stop reading —
 * which is the last thing this badge should teach them to do.
 */
export function headerBadge(input: HeaderBadgeInput): HeaderBadge {
    if (input.phase === 'idle' || !input.version) return { emphasis: false };

    const parts = [input.version];
    // Once there is a plan to count against, the counts *are* the status, and the
    // phase name beside them ("Deploying", "Waiting for the sync") only repeats
    // what the numbers already say — so the badge drops it and shows the version
    // and the progress. Before the sync the phase is the only status there is
    // (the counts would all be zero, which reads as "nothing done" rather than
    // "not known yet"), so it stands in for the progress until the plan lands.
    if (input.progress && input.progress.changed > 0) {
        parts.push(`${input.progress.deployed} of ${input.progress.changed} deployed`);
        if (input.progress.acknowledged > 0) parts.push(`${input.progress.acknowledged} skipped`);
    } else {
        parts.push(PHASE_LABEL[input.phase]);
    }
    return { text: parts.join(' · '), emphasis: input.phase === 'open' || input.phase === 'closing' };
}
